import React, { useEffect, useState } from "react";
import {
  Container,
  CssBaseline,
  Box,
  CircularProgress
} from "@mui/material";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import UploadZone from "./components/UploadZone";
import Transactions from "./pages/Transactions";
import JobHistory from "./pages/JobHistory";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "./firebase";
import SubscriptionPage from "./pages/Subscription";
import PurchaseHistory from "./pages/PurchaseHistory";
import SettingsPage from "./pages/Settings";
import NotificationsPage from "./pages/Notifications";
import apiService, { SubscriptionResponse } from "./services/api";
import { Analytics } from "./components/Analytics";

const getPlanName = (plan: number | string | null) => {
  if (plan === "Free") return "Free";
  switch (plan) {
    case 501900000:
      return "Starter";
    case 501900001:
      return "Business";
    case 501900002:
      return "Enterprise";
    default:
      return "Plan";
  }
};

const getDaysRemainingText = (daysRemaining: number): string => {
  if (daysRemaining <= 0) return "Expired";
  if (daysRemaining === 1) return "1 day left";
  return `${daysRemaining} days left`;
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (u) => {
    setUser(u);
    setCheckingAuth(false);

    if (u) {
      const maxAge = 7 * 24 * 60 * 60; 
      document.cookie = "ef_logged_in=true; max-age=${maxAge}; domain=.extractfast.com; path=/; SameSite=Lax; Secure";
    } else {
      document.cookie = "ef_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.extractfast.com; path=/; SameSite=Lax; Secure";
    }
    // --------------------------------------------------------
    
  });
  return () => unsubscribe();
}, []);

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user) {
        setSubscription(null);
        return;
      }

      try {
        setSubscriptionLoading(true);
        const res = await apiService.getSubscription();
        setSubscription(res || null);
      } catch {
        setSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    loadSubscription();
  }, [user, location.pathname]);


  const handleLogout = async () => {
  try {
    await signOut(auth);
    
    document.cookie = "ef_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.extractfast.com; path=/; SameSite=Lax; Secure";
    
    sessionStorage.clear();
    localStorage.clear();
    navigate("/login");
  } catch (e) {
    console.error("Logout failed", e);
  }
};
 // const handleLogout = async () => {
   // try {
    //  await signOut(auth);
    //  sessionStorage.clear();
    //  localStorage.clear();
    //  navigate("/login");
   // } catch (e) {
   //   console.error("Logout failed", e);
  //  }
  //};

  if (checkingAuth) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const planName = getPlanName(subscription?.plan ?? null);

  const totalAvailable = Number(
    subscription?.totalAvailable ??
      (Number(subscription?.monthlyLimit ?? 0) + Number(subscription?.extraPages ?? 0))
  );

  const pagesUsed = Number(subscription?.pagesUsed ?? 0);
  const daysRemaining = subscription?.daysRemaining ?? 0;
  const isInGracePeriod = subscription?.isInGracePeriod ?? false;
  const gracePeriodRemaining = subscription?.gracePeriodRemaining ?? 0;

  let usageLabel = null;

  if (subscription && totalAvailable > 0) {
    if (isInGracePeriod) {
      usageLabel = `${planName} • Grace Period (${gracePeriodRemaining}d)`;
    } else if (daysRemaining <= 0) {
      usageLabel = `${planName} • Expired - Renew Now`;
    } else {
      usageLabel = `${planName} • ${pagesUsed}/${totalAvailable} pages • ${getDaysRemainingText(daysRemaining)}`;
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <CssBaseline />

      <Analytics />

      {user && (
        <Header
          userEmail={user.email || ""}
          usageLabel={usageLabel}
          subscriptionLoading={subscriptionLoading}
          onLogout={handleLogout}
        />
      )}

      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          flex: 1
        }}
      >
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <UploadZone />
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:jobId"
            element={
              <ProtectedRoute>
                <Transactions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <JobHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/purchase-history"
            element={
              <ProtectedRoute>
                <PurchaseHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Container>

      <Footer />
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;